/**
 * Emissions Trend Chart
 *
 * Stacked area chart showing monthly emissions by scope over time.
 * Uses shadcn Chart (Recharts) with gradient fills.
 */

import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRight, TrendingUp, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrlWithQuery } from '@routes/util';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import { useGetEmissionsByMonthQuery } from '../api/emissions';
import type { MonthlyReport } from '../types/emissions';
import { SCOPE_COLORS } from '../config/scopeBranding';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { convertEmissionValue } from '../utils/emissionsFormatter';

interface EmissionsTrendChartProps {
  initiativeId: string;
  periodStart?: string;
  periodEnd?: string;
  includeChildren?: boolean;
}

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const chartConfig = {
  scope1: {
    label: 'Scope 1',
    color: SCOPE_COLORS.scope1,
  },
  scope2: {
    label: 'Scope 2',
    color: SCOPE_COLORS.scope2,
  },
} satisfies ChartConfig;

interface TrendDataPoint {
  month: string;
  scope1: number;
  scope2: number;
}

const transformDataForChart = (monthlyData: MonthlyReport[], convertValue: (kg: number) => number): TrendDataPoint[] => {
  const grouped: Record<string, Record<string, number>> = {};

  monthlyData.forEach((item) => {
    const key = `${item.year}-${String(item.month).padStart(2, '0')}`;
    if (!grouped[key]) {
      grouped[key] = { scope1: 0, scope2: 0 };
    }
    grouped[key][item.scope] = convertValue(item.total);
  });

  const sortedKeys = Object.keys(grouped).sort();

  return sortedKeys.map((key) => {
    const [year, month] = key.split('-');
    return {
      month: `${monthNames[parseInt(month, 10) - 1]} '${year.slice(2)}`,
      scope1: grouped[key].scope1 || 0,
      scope2: grouped[key].scope2 || 0,
    };
  });
};

export const EmissionsTrendChart = ({ initiativeId, periodStart, periodEnd, includeChildren }: EmissionsTrendChartProps) => {
  const { settings, formatCompact } = useEmissionsFormatter();
  const history = useHistory();
  const viewAllUrl = generateUrlWithQuery(
    emissionsTrackerRoutes.EMISSIONS_INSIGHTS,
    { rootAppPath, initiativeId },
    { tab: 'trends' },
  );
  const defaultStart = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 11);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  }, []);

  const defaultEnd = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  }, []);

  const { data: monthlyData, isLoading } = useGetEmissionsByMonthQuery(
    {
      initiativeId,
      periodStart: periodStart || defaultStart,
      periodEnd: periodEnd || defaultEnd,
      includeChildren,
    },
    { skip: !initiativeId }
  );

  const chartData = useMemo(() => {
    if (!monthlyData?.results || monthlyData.results.length === 0) {
      return null;
    }
    return transformDataForChart(monthlyData.results, (kg) => convertEmissionValue(kg, settings.displayUnit));
  }, [monthlyData, settings.displayUnit]);

  const hasData = chartData && chartData.length > 0;

  return (
    <Card className='tw:h-full'>
      <CardHeader>
        <div className='tw:flex tw:justify-between tw:items-start tw:gap-3'>
          <div>
            <CardTitle>Emissions Trend</CardTitle>
            <CardDescription>Monthly emissions by scope ({formatCompact(0).unit})</CardDescription>
          </div>
          <Button
            variant='link'
            size='sm'
            onClick={() => history.push(viewAllUrl)}
          >
            View all
            <ArrowRight />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='tw:flex tw:items-center tw:justify-center tw:h-[280px]'>
            <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : !hasData ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-[280px] tw:text-muted-foreground'>
            <TrendingUp className='tw:size-10 tw:mb-2 tw:opacity-50' />
            <p className='tw:text-sm'>No trend data available</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className='tw:h-[280px] tw:w-full'>
            <AreaChart data={chartData} accessibilityLayer>
              <defs>
                <linearGradient id='fillScope1' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='var(--color-scope1)' stopOpacity={0.4} />
                  <stop offset='95%' stopColor='var(--color-scope1)' stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id='fillScope2' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='var(--color-scope2)' stopOpacity={0.4} />
                  <stop offset='95%' stopColor='var(--color-scope2)' stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey='month'
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value: string) => value.split(' ')[0]}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value: number) => value.toLocaleString()}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Area
                type='monotone'
                dataKey='scope2'
                stackId='1'
                stroke='var(--color-scope2)'
                strokeWidth={2}
                fill='url(#fillScope2)'
              />
              <Area
                type='monotone'
                dataKey='scope1'
                stackId='1'
                stroke='var(--color-scope1)'
                strokeWidth={2}
                fill='url(#fillScope1)'
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
